/**
 * Modal to change the manager of a pair.
 */
import { useState, useEffect } from 'react';
import { UserCog } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { useUpdatePairManager } from '@/hooks/usePairs';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import type { PairRecord } from '@/types/remote/pair.types';
import { getPairSwapLabel } from './pair-table-utils';
import {
  buildManagerSelectOptions,
  buildUpdatePairManagerDto,
  canSubmitManagerChange,
  initialManagerUserId,
} from './matching-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link ManagerEditModal}. */
interface ManagerEditModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly pair: PairRecord | null;
  readonly onSuccess?: () => void;
}

/** Change the pair manager (coordinator / manager / admin autocomplete). */
export function ManagerEditModal({ open, onOpenChange, pair, onSuccess }: ManagerEditModalProps) {
  const [selectedManagerId, setSelectedManagerId] = useState('');

  const updateManagerMutation = useUpdatePairManager();
  const {
    options: coordinatorOptions,
    isLoading: coordinatorsLoading,
    onSearch,
  } = useAdminAutocomplete({
    roleCodes: ['coordinator_remote', 'manager_remote', 'admin'],
    programName: pair?.program ?? null,
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setSelectedManagerId(initialManagerUserId(pair?.managedBy?.userId));
    }
  }, [open, pair]);

  const canSubmit = canSubmitManagerChange(selectedManagerId, pair?.managedBy?.userId);

  const handleClose = () => {
    onOpenChange(false);
    setSelectedManagerId(initialManagerUserId(pair?.managedBy?.userId));
  };

  const handleSubmit = async () => {
    if (!pair || !canSubmit) return;

    try {
      await updateManagerMutation.mutateAsync({
        pairId: pair.id,
        dto: buildUpdatePairManagerDto(selectedManagerId),
      });

      toast.success('Gestionnaire mis à jour avec succès');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Manager update failed:', error);
      toast.error(getErrorMessage(error));
    }
  };

  if (!pair) return null;

  const managerOptions = buildManagerSelectOptions(pair.managedBy, coordinatorOptions);

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      size="lg"
      contentOverflowVisible
      header={{
        icon: UserCog,
        iconSize: 'md',
        title: 'Modifier le gestionnaire',
        subtitle: getPairSwapLabel(pair),
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      footer={
        <div className="flex gap-2 justify-end w-full">
          <Button
            preset="modal-cancel"
            onClick={handleClose}
            disabled={updateManagerMutation.isPending}
          >
            Annuler
          </Button>
          <Button
            preset="modal-confirm"
            onClick={handleSubmit}
            loading={updateManagerMutation.isPending}
            disabled={!canSubmit || updateManagerMutation.isPending}
          >
            Confirmer
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-2">
        <p className="text-sm text-muted-foreground">
          Le gestionnaire est le coordinateur référent de ce binôme. Il est notamment visible dans
          les filtres et exports de la liste des binômes.
        </p>
        <Autocomplete
          label="Nouveau gestionnaire"
          required
          placeholder="Rechercher un coordinateur, manager ou admin..."
          options={managerOptions}
          value={selectedManagerId}
          onChange={setSelectedManagerId}
          onSearch={onSearch}
          isLoading={coordinatorsLoading}
          minSearchLength={2}
        />
      </div>
    </BaseModal>
  );
}
